import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, Clock, Bell, Settings as SettingsIcon, Sun, Moon, Languages, X, LogOut, CalendarDays, UserPlus, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { authService } from '../services/auth';
import { userSettingsService } from '../services/userSettings';
import api from '../services/api';
import { hasPermission, setCurrentUserCache } from '../utils/roles';

/* ─── helpers ──────────────────────────────────────────────────── */
const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const timeAgo = (isoStr) => {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtDatetime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const date = d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} ${time}`;
};

const fmtEventTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
};

const NOTIFICATION_LOOKBACK_MS = 24 * 60 * 60 * 1000;
const isRecent = (isoStr) => {
  if (!isoStr) return false;
  const time = new Date(isoStr).getTime();
  return Number.isFinite(time) && Date.now() - time <= NOTIFICATION_LOOKBACK_MS;
};

/* ─── icon button helper ─────────────────────────────────────────── */
const IconBtn = ({ onClick, title, children, className = '', isDark = true }) => {
  const baseColor  = isDark ? 'rgba(255,255,255,0.4)'  : '#94a3b8';
  const hoverColor = isDark ? 'rgba(255,255,255,0.85)' : '#0f172a';
  const hoverBg    = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(99,102,241,0.08)';
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${className}`}
      style={{ color: baseColor }}
      onMouseEnter={e => { e.currentTarget.style.background = hoverBg;    e.currentTarget.style.color = hoverColor; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = baseColor; }}
    >
      {children}
    </button>
  );
}

/* ─── component ─────────────────────────────────────────────────── */
const Header = ({ onMenuClick }) => {
  const [currentTime, setCurrentTime]             = useState(new Date());
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile]             = useState(false);
  const [notifications, setNotifications]         = useState([]);
  const [readIds, setReadIds]                     = useState(new Set());
  const [dismissedIds, setDismissedIds]           = useState(new Set());
  const [notificationTimestamps, setNotificationTimestamps] = useState({});
  const [notificationPrefsLoaded, setNotificationPrefsLoaded] = useState(false);
  const [currentUser, setCurrentUser]             = useState(null);
  const notifRef   = useRef(null);
  const profileRef = useRef(null);
  const dismissedIdsRef = useRef(dismissedIds);
  const notificationTimestampsRef = useRef(notificationTimestamps);
  const navigate   = useNavigate();
  const { theme, toggleTheme }       = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const isDark = theme === 'dark';

  const refreshCurrentUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setCurrentUser(data);
      setCurrentUserCache(data);
    } catch {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    dismissedIdsRef.current = dismissedIds;
  }, [dismissedIds]);

  useEffect(() => {
    notificationTimestampsRef.current = notificationTimestamps;
  }, [notificationTimestamps]);

  /* ── fetch notifications ──────────────────────────────────────── */
  const fetchNotifications = useCallback(async () => {
    if (!notificationPrefsLoaded) return;
    const dismissed  = dismissedIdsRef.current;
    const timestamps = notificationTimestampsRef.current;
    const now        = new Date().toISOString();
    const built      = [];
    const newTs      = { ...timestamps };

    try {
      const cr = await api.get('/clients', { params: { page: 1, limit: 5 } });
      const clients = cr.data?.clients || cr.data || [];
      for (const c of clients) {
        const id = `client-${c.id}`;
        const createdAt = c.createdAt || c.registeredAt || c.created_at;
        if (!isRecent(createdAt)) continue;
        if (!dismissed.has(id)) {
          if (!newTs[id]) newTs[id] = createdAt || now;
          built.push({ id, type: 'client', text: `New client: ${c.firstName || ''} ${c.lastName || ''}`.trim(), sub: c.email || '', time: timeAgo(newTs[id]), arrivedAt: newTs[id] });
        }
      }
    } catch { /* ignore */ }

    try {
      const today = localDateStr();
      const er = await api.get('/calendar', { params: { date_from: today, date_to: `${today}T23:59:59` } });
      const events = er.data?.events || er.data || [];
      for (const ev of events) {
        const id = `cal-${ev.id}`;
        if (!dismissed.has(id)) {
          if (!newTs[id]) newTs[id] = now;
          const typeLabel = ev.type ? ev.type.charAt(0).toUpperCase() + ev.type.slice(1) : 'Event';
          built.push({ id, type: 'calendar', text: `${typeLabel}: ${ev.title}`, sub: ev.startAt ? `at ${fmtEventTime(ev.startAt)}` : '', time: timeAgo(newTs[id]), arrivedAt: newTs[id] });
        }
      }
    } catch { /* ignore */ }

    if (JSON.stringify(newTs) !== JSON.stringify(timestamps)) {
      notificationTimestampsRef.current = newTs;
      setNotificationTimestamps(newTs);
      userSettingsService.set('notifications.timestamps', newTs).catch(() => {});
    }
    setNotifications(built);
  }, [notificationPrefsLoaded]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      userSettingsService.get('notifications.dismissed', []),
      userSettingsService.get('notifications.read', []),
      userSettingsService.get('notifications.timestamps', {}),
    ]).then(([dismissed, read, timestamps]) => {
      if (cancelled) return;
      const nextDismissed = new Set(Array.isArray(dismissed) ? dismissed : []);
      const nextTimestamps = timestamps && typeof timestamps === 'object' ? timestamps : {};
      dismissedIdsRef.current = nextDismissed;
      notificationTimestampsRef.current = nextTimestamps;
      setDismissedIds(nextDismissed);
      setReadIds(new Set(Array.isArray(read) ? read : []));
      setNotificationTimestamps(nextTimestamps);
      setNotificationPrefsLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    refreshCurrentUser();
    fetchNotifications();
    return () => clearInterval(timer);
  }, [fetchNotifications, refreshCurrentUser]);

  useEffect(() => {
    const handleCurrentUserUpdated = () => { refreshCurrentUser(); };
    window.addEventListener('current-user-updated', handleCurrentUserUpdated);
    return () => window.removeEventListener('current-user-updated', handleCurrentUserUpdated);
  }, [refreshCurrentUser]);

  useEffect(() => {
    const poll = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(poll);
  }, [fetchNotifications]);

  useEffect(() => {
    const handle = (e) => {
      if (notifRef.current  && !notifRef.current.contains(e.target))  setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const formatTime = (date) =>
    date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  const handleLogout = async () => {
    await authService.logout();
    navigate('/');
  };

  const unreadCount = notifications.filter(n => !readIds.has(n.id)).length;
  const canViewSettings = hasPermission(currentUser, 'View Settings');

  const openNotifications = () => {
    setShowNotifications(v => !v);
    setShowProfile(false);
    const next = new Set([...readIds, ...notifications.map(n => n.id)]);
    setReadIds(next);
    userSettingsService.set('notifications.read', [...next]).catch(() => {});
  };

  const markAllRead = async () => {
    const next = new Set([...readIds, ...notifications.map(n => n.id)]);
    setReadIds(next);
    userSettingsService.set('notifications.read', [...next]).catch(() => {});
    try {
      await markServerClientMessagesRead();
      fetchNotifications();
    } catch { /* ignore */ }
  };

  const dismissNotification = (id) => {
    const dismissed = new Set(dismissedIds);
    dismissed.add(id);
    dismissedIdsRef.current = dismissed;
    setDismissedIds(dismissed);
    userSettingsService.set('notifications.dismissed', [...dismissed]).catch(() => {});
    setNotifications(ns => ns.filter(n => n.id !== id));
  };

  /* ── dropdown base style ─────────────────────────────────────── */
  const dropdownStyle = {
    background: isDark
      ? 'rgba(8, 12, 22, 0.95)'
      : 'rgba(255, 255, 255, 0.97)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(99,102,241,0.15)',
    boxShadow: isDark
      ? '0 24px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
      : '0 24px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(99,102,241,0.08)',
  };

  return (
    <header
      className="sticky top-0 z-30"
      style={{
        background: isDark ? 'rgba(4, 6, 15, 0.75)' : 'rgba(248,250,255,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(99,102,241,0.12)',
      }}
    >
      <div className="flex items-center justify-between px-5 py-3 gap-4">
        {/* Left: Menu Button */}
        <button
          onClick={onMenuClick}
          title="Open menu"
          aria-label="Open menu"
          className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200"
          style={{
            color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.07)',
          }}
        >
          <Menu size={18} />
        </button>

        {/* Right: Controls */}
        <div className="flex items-center gap-1 ml-auto">

          {/* Clock */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg mr-2"
            style={{
              background: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.06)',
              border: isDark ? '1px solid rgba(99,102,241,0.15)' : '1px solid rgba(99,102,241,0.12)',
            }}
          >
            <Clock size={13} style={{ color: '#818cf8' }} />
            <span className="text-xs font-semibold tabular-nums" style={{ color: isDark ? '#c7d2fe' : '#4f46e5' }}>
              {formatTime(currentTime)}
            </span>
          </div>

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            title={language === 'en' ? 'Switch to Russian' : 'Switch to English'}
            aria-label={language === 'en' ? 'Switch to Russian' : 'Switch to English'}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all duration-200"
            style={{
              color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(79,70,229,0.6)',
              background: 'transparent',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(99,102,241,0.08)';
              e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.85)' : '#4f46e5';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(79,70,229,0.6)';
            }}
          >
            <Languages size={14} />
            <span>{language}</span>
          </button>

          {/* Theme Toggle */}
          <IconBtn onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} isDark={isDark}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </IconBtn>

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={openNotifications}
              title="Notifications"
              aria-label="Notifications"
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 relative"
              style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}
              onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(99,102,241,0.08)'; e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.85)' : '#0f172a'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8'; }}
            >
              <Bell size={16} />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    boxShadow: '0 0 8px rgba(99,102,241,0.6)',
                  }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div
                className="absolute right-0 top-full mt-2 w-[min(20rem,calc(100vw-1.5rem))] rounded-xl z-50 overflow-hidden animate-fade-in"
                style={dropdownStyle}
              >
                <div
                  className="flex items-center justify-between px-4 py-3"
                  style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(99,102,241,0.1)' }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}
                      >
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs font-medium transition-colors"
                      style={{ color: '#818cf8' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#a5b4fc'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#818cf8'; }}
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="py-10 text-center">
                      <Bell size={24} className="mx-auto mb-2 opacity-20" style={{ color: isDark ? '#fff' : '#0f172a' }} />
                      <p className="text-sm" style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8' }}>
                        No notifications
                      </p>
                    </div>
                  ) : (
                    notifications.map(n => {
                      const isUnread = !readIds.has(n.id);
                      return (
                        <div
                          key={n.id}
                          className="flex items-start gap-3 px-4 py-3"
                          style={{
                            borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(99,102,241,0.06)',
                            background: isUnread
                              ? (isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.03)')
                              : 'transparent',
                          }}
                        >
                          <div
                            className="mt-0.5 flex-shrink-0 rounded-lg p-1.5"
                            style={n.type === 'client'
                              ? { background: 'rgba(16,185,129,0.12)', color: '#34d399' }
                              : { background: 'rgba(99,102,241,0.12)', color: '#818cf8' }
                            }
                          >
                            {n.type === 'client' ? <UserPlus size={11} /> : <Calendar size={11} />}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium leading-snug" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                              {n.text}
                            </p>
                            {n.sub && (
                              <p className="text-xs mt-0.5" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
                                {n.sub}
                              </p>
                            )}
                            <p className="text-[10px] mt-1" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1' }}
                               title={n.arrivedAt ? fmtDatetime(n.arrivedAt) : ''}>
                              {n.arrivedAt ? fmtDatetime(n.arrivedAt) : n.time}
                            </p>
                          </div>

                          <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                            {isUnread && (
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#818cf8', boxShadow: '0 0 5px rgba(129,140,248,0.8)' }} />
                            )}
                            <button
                              onClick={() => dismissNotification(n.id)}
                              title="Dismiss notification"
                              aria-label="Dismiss notification"
                              className="transition-colors"
                              style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1' }}
                              onMouseEnter={e => { e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.6)' : '#64748b'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1'; }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Calendar shortcut */}
          <IconBtn onClick={() => navigate('/calendar')} title="Calendar" isDark={isDark}>
            <CalendarDays size={16} />
          </IconBtn>

          {canViewSettings && (
            <IconBtn onClick={() => navigate('/settings')} title="Settings" isDark={isDark}>
              <SettingsIcon size={16} />
            </IconBtn>
          )}

          {/* Divider */}
          <div className="w-px h-5 mx-1" style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(99,102,241,0.15)' }} />

          {/* User Profile */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => { setShowProfile(v => !v); setShowNotifications(false); }}
              title="Open profile menu"
              aria-label="Open profile menu"
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl transition-all duration-200 group"
              style={{ background: 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 0 10px rgba(99,102,241,0.35)',
                }}
              >
                {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs font-semibold leading-none truncate max-w-[80px]" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
                  {currentUser?.name || 'User'}
                </p>
                <p className="text-[10px] leading-none mt-0.5 truncate" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#64748b' }}>
                  {currentUser?.role || 'admin'}
                </p>
              </div>
            </button>

            {showProfile && (
              <div
                className="absolute right-0 top-full mt-2 w-56 rounded-xl z-50 overflow-hidden animate-fade-in"
                style={dropdownStyle}
              >
                <div
                  className="px-4 py-3"
                  style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(99,102,241,0.1)' }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        boxShadow: '0 0 14px rgba(99,102,241,0.4)',
                      }}
                    >
                      {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
                        {currentUser?.name || 'User'}
                      </p>
                      <p className="text-xs truncate mt-0.5" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b' }}>
                        ID: {currentUser?.id?.toString().slice(0, 6) || '-'} | {currentUser?.role || 'admin'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-1.5">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
                    style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(239,68,68,0.08)';
                      e.currentTarget.style.color = '#f87171';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
                    }}
                  >
                    <LogOut size={15} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
